import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Card, Empty, Field, PageHeader, Td, Th } from "../../components/common/UI";
import { downloadBlob, useReportMeta } from "../../components/reports/useReportMeta";

const NEW = { name: "", report_key: "executive_summary", saved_report_id: "", relative_period: "previous_month", frequency: "monthly", run_hour: 7, output_format: "pdf",
  delivery_method: "in_app", recipient_user_ids: [], start_date: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" }), end_date: "", is_active: true };

function ScheduleForm({ meta, onSaved }) {
  const [f, setF] = useState(NEW);
  const [users, setUsers] = useState([]);
  const [customs, setCustoms] = useState([]);
  useEffect(() => {
    api.get("/reports/recipients").then((r) => setUsers(r.data.items));
    api.get("/reports/saved", { params: { kind: "custom" } }).then((r) => setCustoms(r.data.items)).catch(() => {});
  }, []);
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async () => {
    try {
      await api.post("/reports/schedules", { ...f, saved_report_id: f.report_key === "custom" ? f.saved_report_id : null, end_date: f.end_date || null, run_hour: Number(f.run_hour), filters: {} });
      toast.success("Schedule created"); setF(NEW); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const sel = (k, opts, tid) => (
    <select className="otms-input" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={tid}>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
  );
  return (
    <Card title="New scheduled report" testId="schedule-form">
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="Name"><input className="otms-input" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="schedule-name-input" /></Field>
        <Field label="Report">{sel("report_key", [...meta.reports.map((r) => [r.key, r.title]), ...(customs.length ? [["custom", "Saved custom report…"]] : [])], "schedule-report-select")}</Field>
        {f.report_key === "custom" && <Field label="Custom report">{sel("saved_report_id", [["", "— choose —"], ...customs.map((c) => [c.id, c.name])], "schedule-custom-select")}</Field>}
        <Field label="Reporting period">{sel("relative_period", [["previous_day", "Previous day"], ["previous_week", "Previous week"], ["previous_month", "Previous month"],
          ["previous_quarter", "Previous quarter"], ["previous_year", "Previous year"], ["year_to_date", "Year to date"]], "schedule-period-select")}</Field>
        <Field label="Frequency">{sel("frequency", ["daily", "weekly", "monthly", "quarterly", "annual"].map((x) => [x, x]), "schedule-frequency-select")}</Field>
        <Field label="Run hour (Asia/Manila)"><input type="number" min="0" max="23" className="otms-input" value={f.run_hour} onChange={(e) => set("run_hour", e.target.value)} data-testid="schedule-hour-input" /></Field>
        <Field label="Format">{sel("output_format", [["pdf", "PDF"], ["xlsx", "Excel (XLSX)"], ["csv", "CSV"]], "schedule-format-select")}</Field>
        <Field label="Delivery" hint="Email delivery is not configured in this deployment.">{sel("delivery_method", [["in_app", "In-app notification + secure download"]], "schedule-delivery-select")}</Field>
        <Field label="Start date"><input type="date" className="otms-input" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="schedule-start-input" /></Field>
        <Field label="End date (optional)"><input type="date" className="otms-input" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="schedule-end-input" /></Field>
        <Field label="Recipients (must be authorized; you if none)" className="md:col-span-2">
          <select multiple className="otms-input h-24" value={f.recipient_user_ids} onChange={(e) => set("recipient_user_ids", [...e.target.selectedOptions].map((o) => o.value))} data-testid="schedule-recipients-select">
            {users.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
          </select>
        </Field>
      </div>
      <Button className="mt-4" size="sm" onClick={submit} data-testid="schedule-create-btn">Create schedule</Button>
    </Card>
  );
}

export function ScheduledReports() {
  const meta = useReportMeta();
  const [items, setItems] = useState([]);
  const load = () => api.get("/reports/schedules").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const runNow = async (s) => {
    try {
      const { data } = await api.post(`/reports/schedules/${s.id}/run`);
      data.status === "failed" ? toast.error(`Run failed: ${data.error}`) : toast.success(`Report generated (${data.status}); recipients notified in-app`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!meta) return null;
  return (
    <div data-testid="scheduled-reports-page">
      <PageHeader eyebrow="Reports and Analytics" title="Scheduled Reports" subtitle="Reports run hourly when due. Each run is recorded with its actual delivery result in Export History." />
      <ScheduleForm meta={meta} onSaved={load} />
      <Card title="Schedules" className="mt-5" testId="schedules-table">
        {!items.length ? <Empty text="No schedules yet." /> : (
          <div className="-m-5 overflow-x-auto"><table className="w-full min-w-[800px]">
            <thead><tr>{["Name", "Report", "Frequency", "Recipients", "Next run", "Last run", "Last status", ""].map((h) => <Th key={h}>{h}</Th>)}</tr></thead>
            <tbody>{items.map((s) => (
              <tr key={s.id} data-testid={`schedule-row-${s.id}`}>
                <Td>{s.name}</Td><Td>{s.report_title}</Td><Td>{s.frequency} · {s.relative_period?.replace(/_/g, " ")} · {s.output_format.toUpperCase()}</Td>
                <Td>{s.recipients.join(", ")}</Td><Td>{fmtDateTime(s.next_run_at)}</Td><Td>{fmtDateTime(s.last_run_at)}</Td>
                <Td><span data-testid={`schedule-status-${s.id}`}>{s.last_status || "—"}</span>{s.last_error && <div className="text-[10px] text-red-600">{s.last_error}</div>}</Td>
                <Td className="whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => runNow(s)} data-testid={`schedule-run-${s.id}`}><Play className="h-3.5 w-3.5" /></Button>{" "}
                  <Button size="sm" variant="ghost" onClick={async () => { if (!window.confirm(`Delete schedule "${s.name}"?`)) return; try { await api.delete(`/reports/schedules/${s.id}`); toast.success("Schedule deleted"); load(); } catch (e) { toast.error(errMsg(e)); } }} data-testid={`schedule-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </Td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
    </div>
  );
}

export function ExportHistory() {
  const [items, setItems] = useState([]);
  const [sp] = useSearchParams();
  const hl = sp.get("highlight");
  useEffect(() => { api.get("/reports/executions").then((r) => setItems(r.data.items)); }, []);
  const dl = (e) => downloadBlob(api.get(`/reports/executions/${e.id}/download`, { responseType: "blob" }), e.file_name).catch((x) => toast.error(x?.response?.status === 410 ? "File expired" : "Download failed"));
  return (
    <div data-testid="export-history-page">
      <PageHeader eyebrow="Reports and Analytics" title="Export History" subtitle="Report runs, exports and scheduled deliveries with their recorded outcome. Generated files expire after the retention period." />
      <Card>
        {!items.length ? <Empty text="No report activity yet." /> : (
          <div className="-m-5 overflow-x-auto"><table className="w-full min-w-[800px]">
            <thead><tr>{["When", "Report", "Source", "Format", "By", "Rows", "Status", "File"].map((h) => <Th key={h}>{h}</Th>)}</tr></thead>
            <tbody>{items.map((e) => (
              <tr key={e.id} className={e.id === hl ? "bg-sky-50" : ""} data-testid={`execution-row-${e.id}`}>
                <Td>{fmtDateTime(e.created_at)}</Td><Td>{e.title}</Td><Td>{e.source}</Td><Td>{e.output_format?.toUpperCase() || "—"}</Td><Td>{e.user || "—"}</Td><Td>{e.row_count}</Td>
                <Td><span data-testid={`execution-status-${e.id}`}>{e.status}</span>{e.error && <div className="text-[10px] text-red-600">{e.error}</div>}</Td>
                <Td>{e.downloadable ? <Button size="sm" variant="outline" onClick={() => dl(e)} data-testid={`execution-download-${e.id}`}><Download className="h-3.5 w-3.5" /></Button> : e.source === "export" ? "Downloaded directly" : "—"}</Td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
    </div>
  );
}

export function ReportDefinitions() {
  const meta = useReportMeta();
  if (!meta) return null;
  return (
    <div data-testid="report-definitions-page">
      <PageHeader eyebrow="Reports and Analytics" title="Report Definitions" subtitle="What each report measures, where the data comes from, and how it is counted." />
      <div className="space-y-4">
        {meta.reports.map((r) => (
          <Card key={r.key} title={r.title} testId={`definition-${r.key}`}>
            <p className="text-xs text-slate-600">{r.description}</p>
            <p className="mt-1 text-[11px] text-slate-500"><b>Source:</b> {r.source} · <b>Permission:</b> {r.perm} · <b>Scope:</b> your LGU only</p>
            <ul className="mt-2 space-y-1 text-[11px] text-slate-600">
              {r.definitions.map((d) => <li key={d.name}><b>{d.name}:</b> {d.description}. Included: {d.inclusion}. Excluded: {d.exclusion}. Date: {d.date_field}. Duplicates: {d.duplicates}.</li>)}
            </ul>
          </Card>
        ))}
      </div>
    </div>
  );
}
